import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api'; // Importar a instância api

interface User {
  id: string;
  name: string;
  login: string;
}

interface AuthContextData {
  user: User | null;
  token: string | null;
  signIn: (login: string, password: string) => Promise<void>;
  signOut: () => void;
}

const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 horas em milissegundos
const USER_STORAGE_KEY = '@MagaluEnvironmentControl:user';
const SESSION_EXPIRATION_KEY = '@MagaluEnvironmentControl:sessionExpiration';
const TOKEN_STORAGE_KEY = '@MagaluEnvironmentControl:token';

interface DecodedToken {
  id: string;
  login: string;
  iat: number;
  exp: number;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const expiration = localStorage.getItem(SESSION_EXPIRATION_KEY);

    if (storedToken && expiration) {
      const expirationTime = parseInt(expiration);
      if (Date.now() < expirationTime) {
        try {
          const decodedToken = jwtDecode<DecodedToken>(storedToken);
          const userData: User = {
            id: decodedToken.id,
            login: decodedToken.login,
            name: 'Usuário'
          };
          setUser(userData);
          setToken(storedToken);
        } catch (error) {
          console.error("Erro ao decodificar token no carregamento:", error);
          localStorage.removeItem(USER_STORAGE_KEY);
          localStorage.removeItem(SESSION_EXPIRATION_KEY);
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
        localStorage.removeItem(SESSION_EXPIRATION_KEY);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    // Verifica expiração da sessão a cada minuto
    const checkSession = setInterval(() => {
      const expiration = localStorage.getItem(SESSION_EXPIRATION_KEY);
      if (expiration && Date.now() >= parseInt(expiration)) {
        signOut();
      }
    }, 60000);

    return () => clearInterval(checkSession);
  }, []);

  const signIn = useCallback(async (login: string, password: string) => {
    try {
      // Usar api.post para o login
      const response = await api.post<{ token: string }>(
        '/api/login', // Caminho relativo correto
        { login, senha: password } // Dados no corpo da requisição
      );

      // Axios retorna dados diretamente em response.data
      const receivedToken = response.data.token;

      if (!receivedToken) {
        throw new Error('Token não recebido na resposta de login');
      }
      
      console.log("Received Token:", receivedToken);
      
      let userData: User;
      try {
        const decodedToken = jwtDecode<DecodedToken>(receivedToken);
        userData = {
          id: decodedToken.id,
          login: decodedToken.login,
          name: decodedToken.login // Usar login como nome padrão
        };
      } catch (error) {
        console.error("Erro ao decodificar token recebido:", error);
        throw new Error('Token recebido inválido.');
      }
      
      setUser(userData);
      setToken(receivedToken);
      
      localStorage.setItem(TOKEN_STORAGE_KEY, receivedToken);
      localStorage.setItem(SESSION_EXPIRATION_KEY, (Date.now() + SESSION_DURATION).toString());

    } catch (error: any) {
      // Tratar erro do Axios
      console.error('Erro no signIn:', error);
      const message = error.response?.data?.error || // Mensagem de erro da API (401, etc)
                      error.message || // Erro genérico do Axios ou de rede
                      'Erro ao fazer login. Verifique suas credenciais.';
      throw new Error(message);
    }
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(SESSION_EXPIRATION_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
} 